import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { signinAccount, clearSigninStatus } from "../slice/signinSlice";
import { setAccount } from "../slice/accountSlice";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import axiosInstance from "../apis/axiosConfig";
import LocationPermissionModal from "./LocationPermissionModal";
import {
  requestLocationPermission,
  isLocationValid,
} from "../utils/locationService";
import { useDarkMode } from "../hooks/useDarkMode";

const Signin = () => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, success, error, data } = useSelector(
    (state) => state.signin
  );

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  /* ==========================
     HELPERS
  ========================== */

  const friendlyFirebaseError = (err) => {
    switch (err?.code) {
      case "auth/wrong-password":
      case "auth/invalid-login-credentials":
        return "Invalid email or password.";
      case "auth/user-not-found":
        return "No account found with this email.";
      case "auth/too-many-requests":
        return "Too many attempts. Please try later.";
      default:
        return err?.message || "Signin failed.";
    }
  };

  const normalizeAccountData = (raw = {}) => {
    const fullName = (raw?.name || "").trim();
    const [first = "", last = ""] = fullName.split(" ");

    return {
      firstname: raw?.firstname || first || "",
      lastname: raw?.lastname || last || "",
      email: raw?.email || "",
      profileImage: raw?.profileImage || raw?.avatar || "",
      ...raw,
    };
  };

  /* ==========================
     FORM HANDLERS
  ========================== */

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setAuthError("");

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      const token = await user.getIdToken();
      localStorage.setItem("token", token);

      // 🔐 Check 2FA
      try {
        const { get2FAStatusApi, send2FALoginCodeApi } = await import(
          "../apis/payments.api"
        );
        const status = await get2FAStatusApi();

        if (status?.data?.two_factor_enabled) {
          await send2FALoginCodeApi();
          localStorage.setItem("pending2FA", formData.email);
          navigate("/login-2fa", {
            state: { email: formData.email, mode: "login" },
          });
          return;
        }
      } catch (err) {
        console.warn("2FA check failed:", err);
      }

      const displayName = user.displayName || "User";
      const nameParts = displayName.split(" ");
      const accountData = {
        firstname: nameParts[0] || "",
        lastname: nameParts.slice(1).join(" ") || "",
        email: user.email,
        profileImage: user.photoURL || "",
        uid: user.uid,
        name: displayName,
      };

      dispatch(setAccount(accountData));
      localStorage.setItem("account", JSON.stringify(accountData));
      localStorage.setItem("user", JSON.stringify(accountData));
      localStorage.setItem("userName", displayName);
      localStorage.setItem("userEmail", user.email);

      requestLocationAfterLogin();
    } catch (err) {
      setAuthError(friendlyFirebaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  /* ==========================
     BACKEND SIGNIN SUCCESS
  ========================== */

  useEffect(() => {
    if (success && data) {
      const accountData = normalizeAccountData(
        data?.data?.user || data?.user || data
      );

      const fullName = `${accountData.firstname} ${accountData.lastname}`.trim() || accountData.email?.split('@')[0] || "User";
      
      dispatch(setAccount(accountData));
      localStorage.setItem("account", JSON.stringify(accountData));
      localStorage.setItem("user", JSON.stringify(accountData));
      localStorage.setItem("userName", fullName);
      localStorage.setItem("userEmail", accountData.email);

      dispatch(clearSigninStatus());
      requestLocationAfterLogin();
    }
  }, [success, data, dispatch]);

  /* ==========================
     GOOGLE LOGIN
  ========================== */

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      setAuthError("");

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const token = await user.getIdToken();

      localStorage.setItem("token", token);

      const displayName = user.displayName || "User";
      const [first = "", ...last] = displayName.split(" ");

      const accountData = {
        firstname: first,
        lastname: last.join(" "),
        email: user.email,
        profileImage: user.photoURL,
        uid: user.uid,
        name: displayName,
      };

      dispatch(setAccount(accountData));
      localStorage.setItem("account", JSON.stringify(accountData));
      localStorage.setItem("user", JSON.stringify(accountData));
      localStorage.setItem("userName", displayName);
      localStorage.setItem("userEmail", user.email);

      requestLocationAfterLogin();
    } catch (err) {
      setAuthError("Google signin failed.");
    } finally {
      setGoogleLoading(false);
    }
  };

  /* ==========================
     LOCATION HANDLING
  ========================== */

  const requestLocationAfterLogin = () => {
    if (isLocationValid()) {
      navigate("/dashboard");
      return;
    }
    setShowLocationModal(true);
  };

  const handleAllowLocation = () => {
    setShowLocationModal(false);
    requestLocationPermission(
      () => navigate("/dashboard"),
      () => navigate("/dashboard")
    );
  };

  const handleSkipLocation = () => {
    setShowLocationModal(false);
    navigate("/dashboard");
  };

  /* ==========================
     UI
  ========================== */

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-green-50"
        }`}
    >
      {/* ===== BACKGROUND CIRCLES ===== */}
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#008300]/10 rounded-full"></div>
      <div className="absolute top-40 right-0 w-64 h-64 bg-[#0B1B3F]/10 rounded-full"></div>
      <div
        className={`p-8 rounded-2xl shadow-lg w-full max-w-md ${isDarkMode ? "bg-gray-800 text-white" : "bg-white"
          }`}
      >
        <h2 className="text-3xl font-semibold text-center mb-6">
          Account <span className="text-[#008300]">Signin</span>
        </h2>

        {(authError || error) && (
          <p className="text-red-500 text-sm mb-3">{authError || error}</p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            name="email"
            placeholder="Email"
            required
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${isDarkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-300 focus:border-green-400 focus:ring-green-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-700 focus:ring-green-200"
              }`}
          />

          <input
            type="password"
            name="password"
            placeholder="Password"
            required
            value={formData.password}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${isDarkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-300 focus:border-green-400 focus:ring-green-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-700 focus:ring-green-200"
              }`}
          />

          <p className="text-right text-sm">
            <Link to="/forget-password" className="text-[#008300] hover:underline">
              Forget Password?
            </Link>
          </p>

          <button
            disabled={loading || submitting}
            className="w-full py-2 bg-[#008300] hover:bg-[#006600] text-white rounded-md"
          >
            {loading || submitting ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="flex items-center my-4">
          <div className="flex-grow border-t" />
          <span className="mx-2 text-gray-500">OR</span>
          <div className="flex-grow border-t" />
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={googleLoading}
          className={`w-full border p-2 rounded-md transition-colors ${isDarkMode
              ? "bg-gray-700 border-gray-500 text-white hover:bg-gray-600 hover:text-white"
              : "bg-white border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900"
            }`}
        >
          Continue with Google
        </button>

        <p className="text-center text-sm mt-4">
          Don’t have an account?{" "}
          <Link to="/signup" className="text-[#008300] hover:underline">
            Sign Up
          </Link>
        </p>
      </div>

      <LocationPermissionModal
        isOpen={showLocationModal}
        onAllow={handleAllowLocation}
        onSkip={handleSkipLocation}
        onClose={handleSkipLocation}
      />
    </div>
  );
};

export default Signin;
